import { useEffect, useState } from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { getNewsBookmarkState, toggleNewsBookmark } from '@/lib/newsBookmarks';

export function NewsBookmarkButton({ articleId }: { articleId: string }) {
  const [bookmarked, setBookmarked] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    getNewsBookmarkState(articleId).then((s) => {
      if (cancel) return;
      setBookmarked(s.bookmarked);
      setCount(s.count);
    });
    return () => { cancel = true; };
  }, [articleId]);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    const next = !bookmarked;
    setBookmarked(next);
    setCount((c) => c + (next ? 1 : -1));
    try {
      await toggleNewsBookmark(articleId, next);
      toast({ title: next ? 'Saved for later' : 'Removed bookmark' });
    } catch (e: any) {
      setBookmarked(!next);
      setCount((c) => c + (next ? -1 : 1));
      toast({ title: 'Could not update bookmark', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant={bookmarked ? 'default' : 'outline'} size="sm" onClick={onClick} className="gap-1.5">
      {bookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
      <span className="hidden sm:inline">{bookmarked ? 'Saved' : 'Save'}</span>
      {count > 0 && <span className="text-xs opacity-70">{count}</span>}
    </Button>
  );
}
